import { useMemo, useState } from "react";
import dayjs, { Dayjs } from "dayjs";

export type DatePreset = "today" | "last7" | "last30" | "thisMonth" | "lastMonth" | "custom";

export interface DateRange {
  startDate: string;
  endDate: string;
  label: string;
  preset: DatePreset;
  customStart: Dayjs | null;
  customEnd: Dayjs | null;
  setPreset: (preset: DatePreset) => void;
  setCustomStart: (date: Dayjs | null) => void;
  setCustomEnd: (date: Dayjs | null) => void;
}

const fmt = (date: Dayjs) => date.format("YYYY-MM-DD");

export const useDateRange = (): DateRange => {
  const [preset, setPreset] = useState<DatePreset>("last30");
  const [customStart, setCustomStart] = useState<Dayjs | null>(dayjs().startOf("month"));
  const [customEnd, setCustomEnd] = useState<Dayjs | null>(dayjs());

  return useMemo(() => {
    const now = dayjs();
    let start: Dayjs;
    let end: Dayjs;
    let label: string;

    switch (preset) {
      case "today":
        start = now;
        end = now;
        label = start.format("D MMM YYYY");
        break;
      case "last7":
        start = now.subtract(6, "day");
        end = now;
        label = `${start.format("D MMM YYYY")} – ${end.format("D MMM YYYY")}`;
        break;
      case "last30":
        start = now.subtract(29, "day");
        end = now;
        label = `${start.format("D MMM YYYY")} – ${end.format("D MMM YYYY")}`;
        break;
      case "lastMonth": {
        const target = now.subtract(1, "month");
        start = target.startOf("month");
        end = target.endOf("month");
        label = start.format("MMMM YYYY");
        break;
      }
      case "custom": {
        start = customStart ?? now.startOf("month");
        end = customEnd ?? now;
        label = `${start.format("D MMM YYYY")} – ${end.format("D MMM YYYY")}`;
        break;
      }
      case "thisMonth":
      default:
        start = now.startOf("month");
        end = now.endOf("month");
        label = start.format("MMMM YYYY");
        break;
    }

    return {
      startDate: fmt(start),
      endDate: fmt(end),
      label,
      preset,
      customStart,
      customEnd,
      setPreset,
      setCustomStart,
      setCustomEnd,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, customStart, customEnd]);
};
